import { Instagram, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import instaQr from "@/assets/qr_insta.webp";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const INSTAGRAM_URL = "https://www.instagram.com/elfaza_vibes/";
const TOTAL_SLOTS = 3;

const fallbackPosts: DisplayPost[] = [];

function FadeInSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

type DisplayPost = { id: string; src: string; alt: string; url: string };

export const InstagramSection = () => {
  const [posts, setPosts] = useState<DisplayPost[]>(fallbackPosts);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from("social_posts")
        .select("*")
        .eq("platform", "instagram")
        .eq("is_visible", true)
        .order("display_order", { ascending: true })
        .limit(3);
      if (data && data.length > 0) {
        setPosts(data.map((p) => ({
          id: p.id,
          src: p.thumbnail_url || "",
          alt: p.caption || "Post Instagram EL FAZA",
          url: p.post_url,
        })));
      }
    };
    load();
  }, []);

  const emptySlots = Math.max(0, TOTAL_SLOTS - posts.length);

  return (
    <section className="relative py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30Z' fill='none' stroke='%23c2410c' stroke-width='0.5'/%3E%3C/svg%3E")`,
        backgroundSize: "60px 60px",
      }} />
      <div className="absolute top-1/3 right-0 w-72 h-72 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <FadeInSection className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-body mb-6">
            <Instagram className="w-3.5 h-3.5" /> @elfaza_vibes
          </span>
          <h2 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-4 leading-tight">
            Rejoins la communauté
            <br />
            <span className="text-gradient">EL FAZA</span>
          </h2>
          <p className="text-muted-foreground font-body text-base md:text-lg max-w-lg mx-auto leading-relaxed">
            Découvre nos créations, nos coulisses et l'univers EL FAZA sur Instagram 🇹🇳✨
          </p>
        </FadeInSection>

        {/* QR + Posts on same row */}
        <FadeInSection delay={150} className="max-w-5xl mx-auto mb-12">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 items-start">
            {/* QR */}
            <div className="flex flex-col items-center gap-2">
              <div className="aspect-square w-full bg-card/60 backdrop-blur-sm rounded-2xl p-4 border border-border/60 hover:shadow-lg hover:border-primary/20 transition-all duration-300 flex items-center justify-center">
                <img src={instaQr} alt="QR code Instagram EL FAZA" className="w-full h-full object-contain" width="144" height="144" />
              </div>
              <p className="text-[11px] text-muted-foreground font-body">Scanne pour nous suivre 📱</p>
            </div>
            {/* Posts */}
            {posts.map((post) => (
              <button
                key={post.id}
                onClick={() => window.open(post.url, "_blank", "noopener,noreferrer")}
                className="group relative aspect-square rounded-xl overflow-hidden border border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg"
              >
                <img
                  src={post.src}
                  alt={post.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                  <span className="text-white text-xs font-body flex items-center gap-1.5">
                    <Instagram className="w-3.5 h-3.5" /> Voir sur Instagram
                  </span>
                </div>
              </button>
            ))}
            {/* Empty slots */}
            {Array.from({ length: emptySlots }).map((_, i) => (
              <div
                key={`empty-${i}`}
                className="aspect-square rounded-xl border-2 border-dashed border-border/40 bg-muted/20 flex flex-col items-center justify-center gap-2 text-muted-foreground/30"
              >
                <Instagram className="w-5 h-5" />
              </div>
            ))}
          </div>
        </FadeInSection>

        {/* CTA */}
        <FadeInSection delay={300} className="flex justify-center">
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="rounded-xl gap-2 px-6 border-primary/30 text-primary hover:bg-primary/5 font-body">
              <Instagram className="w-4 h-4" />
              Suivre sur Instagram
              <ArrowRight className="w-4 h-4" />
            </Button>
          </a>
        </FadeInSection>
      </div>
    </section>
  );
};